import { faSave, faTag, faTrashAlt, faUndo } from "@fortawesome/pro-regular-svg-icons";
import { faEdit, faExclamationTriangle, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColorConfigs } from "@yodlee/common-components/dist/configurations";
import { ResponsiveEnums } from "@yodlee/common-components/dist/types/enumerations";
import Authentication from "@yodlee/common-components/dist/types/interfaces/authentication";
import { NotificationUtils } from "@yodlee/common-components/dist/utilities";
import { Button, Col, Drawer, Icon, Input, List, Modal, Row, Spin, Tooltip, Typography } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { LabelConfigs } from "../configurations";
import { ApplicationContext } from "../context";
import { AmplitudeEventsUtils } from "../utilities";
import { tag as TagDataUtils } from "../utilities/data";

interface IProps {
    authentication: Authentication,
    disabled: boolean,
    showButton?: boolean,
    onOpen?: () => void,
    visible?: boolean,
    onClose?: () => void
}

const ManageTagComponent: React.FC<IProps> = ({ authentication, disabled, showButton = true, onOpen, visible: externalVisible, onClose: externalOnClose }) => {
    const { onload } = useContext(ApplicationContext);
    const [internalVisible, setInternalVisible] = useState(false);
    const [tags, setTags] = useState<Array<any>>([]);
    const [selectedTagIndex, setSelectedTagIndex] = useState<number>(-1);
    const [editableTagIndex, setEditableTagIndex] = useState<number>(-1);
    const [isTagsLoading, setIsTagsLoading] = useState<boolean>(false);
    const [newTagName, setNewTagName] = useState<string>("");
    const [isCreatingNewTag, setIsCreatingNewTag] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const maxTagCount = onload?.maxSrTagCount || 30;

    const fetchTags = () => {
        setIsTagsLoading(true);
        TagDataUtils(authentication).getAll()
            .then((tagsData) => {
                const tagsArray = Array.isArray(tagsData) ? tagsData : [];
                setTags(tagsArray);
            })
            .catch((error) => {
                console.error("Error fetching tags:", error);
                setTags([]);
            })
            .finally(() => {
                setIsTagsLoading(false);
            });
    }

    const removeUnsavedNewTag = () => {
        if (tags.length > 0 && tags[0]?.id === -1) {
            setTags(tags.filter((_, idx) => idx !== 0));
            setIsCreatingNewTag(false);
            setEditableTagIndex(-1);
            setSelectedTagIndex(-1);
            setNewTagName("");
        }
    }

    const handleTagSelect = (index: number) => {
        // If there's an unsaved new tag, remove it first
        if (isCreatingNewTag && tags.length > 0 && tags[0]?.id === -1) {
            removeUnsavedNewTag();
        }
        // If clicking the same tag, deselect it
        if (selectedTagIndex === index) {
            setSelectedTagIndex(-1);
            setEditableTagIndex(-1);
        } else {
            setSelectedTagIndex(index);
            setEditableTagIndex(-1);
        }
    }

    const handleTagEditToggle = (index: number, isEditable: boolean) => {
        if (isEditable) {
            // If there's an unsaved new tag, remove it first
            if (isCreatingNewTag && tags.length > 0 && tags[0]?.id === -1) {
                removeUnsavedNewTag();
            }
            // Ensure the tag is selected before editing
            if (selectedTagIndex !== index) {
                setSelectedTagIndex(index);
            }
            setEditableTagIndex(index);
            setNewTagName(tags[index]?.name || "");
            setIsCreatingNewTag(false);
        } else {
            // If canceling a new tag creation, remove it from the list
            if (isCreatingNewTag && index === 0 && tags[0]?.id === -1) {
                removeUnsavedNewTag();
            } else {
                setEditableTagIndex(-1);
                setNewTagName("");
            }
        }
    }

    const handleTagRename = (tagId: number, tagName: string) => {
        const trimmedTagName = tagName?.trim();
        if (!trimmedTagName || trimmedTagName === tags.find(t => t.id === tagId)?.name) {
            setEditableTagIndex(-1);
            setNewTagName("");
            setErrorMessage("");
            return;
        }

        setErrorMessage("");
        setIsTagsLoading(true);
        TagDataUtils(authentication).update(tagId, { name: trimmedTagName })
            .then(() => {
                fetchTags();
                setEditableTagIndex(-1);
                setNewTagName("");
                setErrorMessage("");
                // Keep the tag selected after rename
            })
            .catch((error) => {
                const errorMsg = error?.response?.data?.message || "Unable to update tag";
                setErrorMessage(errorMsg);
                setIsTagsLoading(false);
            });
    }

    const handleTagDelete = (tagId: number, tagName?: string) => {
        const tagToDelete = tags.find(t => t.id === tagId);
        const displayName = tagName || tagToDelete?.name || "this tag";
        
        Modal.confirm({
            keyboard: false,
            title:  <Typography.Title className="color-blue-5" level={2}>
                        Are you sure you want to delete "{displayName}" tag?
                        <br />
                    </Typography.Title>,
            icon: null,
            content: <span className="color-gray-5">Deleting this tag will disassociate it with existing service requests.</span>,
            okText: "Cancel",
            cancelText: "Delete",
            onCancel: () => {
                setIsTagsLoading(true);
                TagDataUtils(authentication).delete(tagId)
                    .then(() => {
                        setSelectedTagIndex(-1);
                        setEditableTagIndex(-1);
                        fetchTags();
                    })
                    .catch((error) => {
                        NotificationUtils.warning("Request failed.", error?.response?.data?.message || "Unable to delete tag", "ghost");
                        setIsTagsLoading(false);
                    });
            },
            style: { maxWidth: "350px" },
            cancelButtonProps: { className: "color-pink border-pink margin-right-xs" },
            okButtonProps: { type: "ghost" },
        });
    }

    const handleTagCreate = () => {
        // Clear any previous selection and editing state
        setSelectedTagIndex(-1);
        setEditableTagIndex(-1);
        setNewTagName("");
        setIsCreatingNewTag(true);
        // Add a temporary new tag at the top of the list
        setTags([{ id: -1, name: "" }, ...tags]);
        // Select and make the new tag editable
        setSelectedTagIndex(0);
        setEditableTagIndex(0);
    }

    const handleSaveNewTag = () => {
        const trimmedTagName = newTagName?.trim();
        if (!trimmedTagName) {
            // If empty, remove the temporary tag
            setTags(tags.filter((_, index) => index !== 0));
            setIsCreatingNewTag(false);
            setEditableTagIndex(-1);
            setNewTagName("");
            setErrorMessage("");
            return;
        }

        setErrorMessage("");
        setIsTagsLoading(true);
        TagDataUtils(authentication).create({ name: trimmedTagName })
            .then(() => {
                setIsCreatingNewTag(false);
                setEditableTagIndex(-1);
                setNewTagName("");
                setErrorMessage("");
                fetchTags();
            })
            .catch((error) => {
                const errorMsg = error?.response?.data?.message || "Unable to create tag";
                setErrorMessage(errorMsg);
                setIsTagsLoading(false);
                // Keep the temporary tag on error so user can fix it
            });
    }

    const visible = externalVisible !== undefined ? externalVisible : internalVisible;

    const handleDrawerClose = () => {
        setEditableTagIndex(-1);
        setSelectedTagIndex(-1);
        setNewTagName("");
        setIsCreatingNewTag(false);
        setErrorMessage("");
        // Remove temporary new tag if exists
        if (tags.length > 0 && tags[0]?.id === -1) {
            setTags(tags.filter((_, index) => index !== 0));
        }
        if (externalVisible === undefined) {
            setInternalVisible(false);
        }
        if (externalOnClose) {
            externalOnClose();
        }
    }

    useEffect(() => {
        if (visible) {
            fetchTags();
        }
    }, [visible]);

    const TagRow = ({ tag, index }: { tag: any; index: number }) => {
        const isSelected = index === selectedTagIndex;
        const isEditable = index === editableTagIndex;
        const currentTag = tags[index];
        const isNewTag = isCreatingNewTag && index === 0 && currentTag?.id === -1;

        return (
            <Row>
                <Col span={(isSelected || isNewTag) ? 21 : 24} className="role-name trim-overflown-text">
                    {(isEditable || isNewTag) ? (
                        <Input
                            className="rolegrp-row background-blue-5 color-white"
                            style={{ 
                                height: "29px", 
                                border: "none",
                                width: "100%"
                            }}
                            maxLength={90}
                            placeholder={isNewTag ? "New tag" : "Name tag"}
                            defaultValue={isNewTag ? "" : currentTag?.name}
                            value={newTagName}
                            onChange={(e) => {
                                setNewTagName(e.target.value);
                                setErrorMessage("");
                            }}
                            onPressEnter={() => isNewTag ? handleSaveNewTag() : handleTagRename(currentTag?.id, newTagName)}
                            autoFocus
                        />
                    ) : (
                        currentTag?.name
                    )}
                </Col>
                {((isSelected && !isNewTag) || isNewTag) && (
                    <Col span={3} className="role-icon" style={(isEditable || isNewTag) ? { textAlign: "right", paddingTop: "4px" } : {}}>
                        {!isEditable && !isNewTag && (
                            <>
                                <FontAwesomeIcon 
                                    icon={faEdit} 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleTagEditToggle(index, true);
                                    }} 
                                    style={{ cursor: "pointer" }}
                                />
                                <span className="padding-left-md" />
                                <FontAwesomeIcon 
                                    icon={faTrashAlt} 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleTagDelete(currentTag?.id, currentTag?.name);
                                    }} 
                                    style={{ cursor: "pointer" }}
                                />
                            </>
                        )}
                        {(isEditable || isNewTag) && (
                            <>
                                <FontAwesomeIcon 
                                    icon={faUndo} 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isNewTag) {
                                            removeUnsavedNewTag();
                                        } else {
                                            handleTagEditToggle(index, false);
                                        }
                                    }} 
                                    style={{ cursor: "pointer" }}
                                />
                                <span className="padding-left-md" />
                                <FontAwesomeIcon 
                                    icon={faSave} 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        isNewTag ? handleSaveNewTag() : handleTagRename(currentTag?.id, newTagName);
                                    }} 
                                    style={{ cursor: "pointer" }}
                                />
                                <span className="padding-left-xs" />
                            </>
                        )}
                    </Col>
                )}
            </Row>
        );
    };

    const manageTagModal =
        <Drawer
            width="37vw"
            title={
                <React.Fragment>
                    <Icon
                        className="font-size-md"
                        style={{ verticalAlign: "inherit", color: ColorConfigs.blackTransparent55 }}
                        component={() => <FontAwesomeIcon icon={faTag} />}
                    />
                    <span className="margin-left-xs" />
                    Tags
                    <Typography.Paragraph style={{ fontSize: "12px", color: ColorConfigs.blackTransparent55 }}>
                        {maxTagCount} available tags ({tags.length}/{maxTagCount} used)
                    </Typography.Paragraph>
                </React.Fragment>
            }
            visible={visible}
            placement="right"
            className="no-padding-drawer drawer-type-1"
            destroyOnClose={true}
            onClose={handleDrawerClose}
            bodyStyle={{ padding: 0 }}
        >
            <Spin spinning={isTagsLoading} indicator={<Icon type="loading" />}>
                <div style={{ padding: "1rem" }}>
                    {errorMessage && (
                        <>
                            <Row 
                                style={{
                                    padding: "0.5rem",
                                    borderWidth: "1px",
                                    borderColor: ColorConfigs.blackTransparent25,
                                    borderRadius: "6px",
                                    borderStyle: "solid",
                                }}
                            >
                                    <Col span={3} >
                                        <FontAwesomeIcon 
                                            icon={faExclamationTriangle} 
                                            style={{ 
                                                color: ColorConfigs.red, 
                                                fontSize: "30px",
                                                margin: "0.5rem"
                                            }} 
                                        />
                                        </Col>
                                        <Col span={21}>
                                        <div className="font-size-md">
                                            <b>{errorMessage}</b>
                                            <br />
                                            Please use a different name for this tag.
                                        </div>
                                    </Col>
                            </Row>
                            <br/>
                        </>
                    )}
                    <List
                        itemLayout="vertical"
                        className="managegrprls-items-list role-names"
                        dataSource={tags}
                        renderItem={(tag: any, index: number) => (
                            <List.Item
                                key={tag?.id || index}
                                onClick={() => {
                                    if (!isCreatingNewTag || index !== 0 || tag?.id !== -1) {
                                        handleTagSelect(index);
                                    }
                                }}
                                style={{ 
                                    border: 0, 
                                    padding: ((index === editableTagIndex && index === selectedTagIndex) || (isCreatingNewTag && index === 0 && tag?.id === -1)) ? "4px 8px" : "8px",
                                    cursor: "pointer"
                                }}
                                className={(index === selectedTagIndex || (isCreatingNewTag && index === 0 && tag?.id === -1)) ? "role-selected" : ""}
                            >
                                <TagRow tag={tag} index={index} />
                            </List.Item>
                        )}
                    />
                    <div style={{ 
                        padding: "16px 0", 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "center",
                        borderTop: "1px solid #f0f0f0",
                        marginTop: "1rem"
                    }}>
                        {tags.length >= maxTagCount ? (
                            <Tooltip title={`You have reached the maximum of ${maxTagCount} tags.`}>
                                <Button
                                    disabled={true}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    <span className="margin-left-xs" />
                                    Add tag
                                </Button>
                            </Tooltip>
                        ) : (
                            <Button
                                onClick={() => handleTagCreate()}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <span className="margin-left-xs" />
                                Add tag
                            </Button>
                        )}
                        <Button
                            type="primary"
                            onClick={handleDrawerClose}
                        >
                            Done
                        </Button>
                    </div>
                </div>
            </Spin>
        </Drawer>

    const handleOpen = () => {
        if (externalVisible === undefined) {
            setInternalVisible(true);
        }
        if (onOpen) {
            onOpen();
        } else {
            AmplitudeEventsUtils.supportGroupsClick();
        }
    };

    return (
        <React.Fragment>
            {manageTagModal}
            {showButton && (
                <Button disabled={disabled} type="default"
                    onClick={handleOpen}>
                    <Icon className="font-size-lg" component={() => <FontAwesomeIcon icon={faTag} />} /> {LabelConfigs.MANAGE_TAG}
                </Button>
            )}
        </React.Fragment>
    )
}

export default ManageTagComponent;

